import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Calendar, Clock, Tag, ArrowRight, Rss, Loader2 } from "lucide-react";
import { supabase } from "@/lib/supabase";

function estimateReadTime(content: string) {
  return Math.max(1, Math.round(content.split(/\s+/).length / 200));
}

const Blog = () => {
  const [posts, setPosts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = "Blog | VCapz Media — Insights & Ideas";
    const desc = "Expert perspectives on digital marketing, content creation, social media strategy, and brand growth from VCapz Media.";
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement("meta");
      metaDesc.setAttribute("name", "description");
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute("content", desc);
  }, []);

  useEffect(() => {
    const fetchPosts = async () => {
      const { data, error } = await supabase
        .from('BlogPost')
        .select('*')
        .eq('published', true)
        .order('publishedAt', { ascending: false });

      if (!error && data) {
        setPosts(data);
      }
      setLoading(false);
    };
    fetchPosts();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-background pt-28 flex items-center justify-center">
        <Loader2 className="animate-spin text-primary" size={32} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pt-28 pb-20">
      <div className="container max-w-6xl mx-auto">

        {/* Header */}
        <div className="text-center mb-20">
          <span className="text-primary font-bold tracking-widest uppercase text-sm mb-4 block flex items-center justify-center gap-2">
            <Rss size={14} /> VCapz Media Blog
          </span>
          <h1 className="text-5xl md:text-7xl font-black text-white mb-6">INSIGHTS &amp; IDEAS</h1>
          <p className="text-xl text-text-muted max-w-2xl mx-auto">
            Expert perspectives on digital marketing, content creation, and brand growth.
          </p>
        </div>

        {posts.length === 0 ? (
          <div className="text-center py-24 glass-card border-dashed border-white/10">
            <Rss size={48} className="text-text-muted mx-auto mb-4 opacity-20" />
            <h2 className="text-2xl font-bold text-white mb-2">No Posts Yet</h2>
            <p className="text-text-muted">Check back soon — great content is on the way.</p>
          </div>
        ) : (
          <>
            {/* Featured post */}
            {posts[0] && (
              <Link to={`/blog/${posts[0].slug}`} className="group block glass-card border-white/5 overflow-hidden mb-12 hover:border-primary/30 transition-all duration-300">
                <div className="md:flex">
                  {posts[0].imageUrl && (
                    <div className="relative md:w-1/2 aspect-video md:aspect-auto h-64 md:h-auto overflow-hidden">
                      <img src={posts[0].imageUrl} alt={posts[0].title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent to-background/50 hidden md:block" />
                    </div>
                  )}
                  <div className={`p-10 flex flex-col justify-center ${posts[0].imageUrl ? "md:w-1/2" : "w-full"}`}>
                    <span className="text-xs font-bold text-primary bg-primary/10 border border-primary/20 px-3 py-1 rounded-full inline-block mb-4 w-fit">Featured</span>
                    <h2 className="text-3xl md:text-4xl font-black text-white mb-4 group-hover:text-primary transition-colors leading-tight">{posts[0].title}</h2>
                    {posts[0].excerpt && <p className="text-text-muted mb-6 leading-relaxed line-clamp-3">{posts[0].excerpt}</p>}
                    <div className="flex items-center gap-6 text-xs text-text-muted mb-6">
                      {posts[0].publishedAt && (
                        <span className="flex items-center gap-1"><Calendar size={12} />{new Date(posts[0].publishedAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</span>
                      )}
                      <span className="flex items-center gap-1"><Clock size={12} />{estimateReadTime(posts[0].content)} min read</span>
                    </div>
                    <span className="flex items-center gap-2 text-primary font-semibold text-sm group-hover:gap-4 transition-all">Read Article <ArrowRight size={16} /></span>
                  </div>
                </div>
              </Link>
            )}

            {/* Rest of posts */}
            {posts.length > 1 && (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {posts.slice(1).map((post) => {
                  const tags = post.tags ? post.tags.split(",").map((t) => t.trim()).filter(Boolean) : [];
                  return (
                    <Link key={post.id} to={`/blog/${post.slug}`} className="group glass-card border-white/5 overflow-hidden flex flex-col hover:border-primary/30 transition-all duration-300">
                      {post.imageUrl ? (
                        <div className="relative aspect-video overflow-hidden">
                          <img src={post.imageUrl} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                        </div>
                      ) : (
                        <div className="aspect-video bg-gradient-to-br from-primary/20 to-accent/10" />
                      )}
                      <div className="p-6 flex flex-col flex-1">
                        {tags.length > 0 && (
                          <div className="flex flex-wrap gap-1.5 mb-3">
                            {tags.slice(0, 2).map((tag) => (
                              <span key={tag} className="flex items-center gap-1 text-[10px] font-bold text-primary bg-primary/10 px-2 py-0.5 rounded-full">
                                <Tag size={8} />{tag}
                              </span>
                            ))}
                          </div>
                        )}
                        <h2 className="text-xl font-bold text-white mb-3 group-hover:text-primary transition-colors leading-snug line-clamp-2">{post.title}</h2>
                        {post.excerpt && <p className="text-sm text-text-muted line-clamp-2 mb-4 flex-1">{post.excerpt}</p>}
                        <div className="flex items-center justify-between text-xs text-text-muted mt-auto pt-4 border-t border-white/5">
                          <span className="flex items-center gap-1">
                            <Calendar size={12} />
                            {post.publishedAt ? new Date(post.publishedAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "Draft"}
                          </span>
                          <span className="flex items-center gap-1"><Clock size={12} />{estimateReadTime(post.content)} min</span>
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Blog;
